import { cartNumber, cartSchedule } from './cartItem';
import { scheduledStoreDate, storeCalendar } from './storeTimezone';
import type { StoreTimezone } from './storeTimezone';

export type CartSchedule = { data?: string | null; hora?: string | null } | 'IMEDIATO' | null;
export type CartTimeOptions = {
  schedule?: CartSchedule;
  now?: Date;
  company?: StoreTimezone | null;
};
export type CartAvailabilityContext = Readonly<{
  date: Date | null;
  day: number;
  time: string;
}>;
export type ProductAvailability = {
  ativo?: boolean;
  internet?: boolean;
  controlarEstoque?: boolean;
  quantidadeEstoque?: string | number | null;
  disponibilidadeDelivery?: {
    dias?: number[];
    horarios?: { inicio: string; fim: string }[];
    horariosPorDia?: { dia: number; horarios: { inicio: string; fim: string }[] }[];
  } | null;
};

// Usa o mesmo fuso da loja que a entrega, independentemente do aparelho.
export function cartReferenceDate({ schedule, now = new Date(), company }: CartTimeOptions = {}): Date | null {
  if (!schedule || schedule === 'IMEDIATO') return now;
  const day = String(schedule.data || '').match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  const time = String(schedule.hora || '').match(/(?:T|^)(\d{2}:\d{2})/)?.[1];
  if (!day || !time) return null;
  try {
    return scheduledStoreDate(company, day, time);
  } catch {
    return null;
  }
}

// Um contexto por avaliação mantém todos os itens no mesmo instante e evita
// converter o agendamento e o calendário da loja a cada produto/complemento.
export function createCartAvailabilityContext({ schedule = cartSchedule(), now, company }: CartTimeOptions = {}): CartAvailabilityContext {
  const date = cartReferenceDate({ schedule, now, company });
  if (!date) return { date: null, day: -1, time: '' };
  const calendar = storeCalendar(company, date);
  return { date, day: calendar.dayOfWeek, time: calendar.dateTime.slice(11, 16) };
}

type CartAvailabilityOptions = CartTimeOptions & { context?: CartAvailabilityContext };

export function cartProductAvailable(product: ProductAvailability | null | undefined, options: CartAvailabilityOptions = {}): boolean {
  if (!product || product.ativo === false || product.internet !== true) return false;
  const { date, day, time } = options.context ?? createCartAvailabilityContext(options);
  if (!date) return false;
  const availability = product.disponibilidadeDelivery ?? {};
  const days = Array.isArray(availability.dias) ? availability.dias : [];
  const ranges = Array.isArray(availability.horarios) ? availability.horarios : [];
  const validDay = (value: number) => !days.length || days.includes(value);
  if (Object.prototype.hasOwnProperty.call(availability, 'horariosPorDia')) {
    const perDay = availability.horariosPorDia;
    const valid = Array.isArray(perDay) && perDay.some((entry) => {
      if (!Number.isInteger(entry?.dia) || entry.dia < 0 || entry.dia > 6 || !validDay(entry.dia)) return false;
      return Array.isArray(entry.horarios) && entry.horarios.some((range) => {
        if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(range?.inicio) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(range?.fim)) return false;
        return range.inicio > range.fim
          ? (day === entry.dia && time >= range.inicio) || (day === (entry.dia + 1) % 7 && time <= range.fim)
          : day === entry.dia && time >= range.inicio && time <= range.fim;
      });
    });
    return valid && (!product.controlarEstoque || cartNumber(product.quantidadeEstoque) > 0);
  }
  const validTime = ranges.length ? ranges.some((range) => {
    if (!/^\d{2}:\d{2}$/.test(range?.inicio) || !/^\d{2}:\d{2}$/.test(range?.fim)) return false;
    const overnight = range.inicio > range.fim;
    const scheduleDay = overnight && time <= range.fim ? (day + 6) % 7 : day;
    return validDay(scheduleDay) && (overnight ? time >= range.inicio || time <= range.fim
      : time >= range.inicio && time <= range.fim);
  }) : validDay(day);
  return validTime && (!product.controlarEstoque || cartNumber(product.quantidadeEstoque) > 0);
}
